import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import RequiredMark from '@/shein-platform/components/RequiredMark';
import AttributeField from '@/shein-platform/modules/products/components/AttributeField';
import CategoryCascaderSelect from '@/shein-platform/components/CategoryCascaderSelect';
import {
  buildProductAttributeTemplatePayload,
  countFilledProductAttributes,
  alignAttributesToCategoryTemplate,
} from '@/shein-platform/utils/sheinProductAttributeTemplate';
import { parseAttributeTemplate, partitionProductAttrsForDisplay } from '@/shein-platform/utils/sheinAttributeTemplate';
import { layoutAttributeFields } from '@/shein-platform/utils/sheinAttributeLayout';
import { parseCustomAttributePermission } from '@/shein-platform/utils/sheinCustomAttribute';
import SheinSwitch from '@/shein-platform/components/SheinSwitch';
import { flattenLeafCategories } from '@/shein-platform/utils/sheinCategoryTree';
import { nextTemplatePriority } from '@/shein-platform/utils/sheinTemplatePriority';
import {
  conflictToastMessage,
  isTemplateVersionConflictError,
  parseTemplateVersion,
} from '@/shein-platform/utils/sheinTemplateVersionGuard';
import TemplateVersionConflictMark from '@/shein-platform/modules/products/components/TemplateVersionConflictMark';
import { sheinPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';
import { showToast } from '@/utils/toast';

function fetchAttrTemplateDetail(officialMode, shopId, templateId, templateScope) {
  if (officialMode) {
    return adminAPI.getOfficialSheinAttributeTemplate(templateId);
  }
  return sheinPlatformAPI.getProductAttributeTemplate(shopId, templateId, { scope: templateScope });
}

function fetchAttrTemplateList(officialMode, shopId, params) {
  if (officialMode) {
    return adminAPI.listOfficialSheinAttributeTemplates(params);
  }
  return sheinPlatformAPI.listProductAttributeTemplates(shopId, params);
}

function saveAttrTemplate(officialMode, shopId, templateId, body) {
  if (officialMode) {
    return templateId
      ? adminAPI.updateOfficialSheinAttributeTemplate(templateId, body)
      : adminAPI.createOfficialSheinAttributeTemplate(body);
  }
  return templateId
    ? sheinPlatformAPI.updateProductAttributeTemplate(shopId, templateId, body)
    : sheinPlatformAPI.createProductAttributeTemplate(shopId, body);
}

function fetchCategoryAttributeSchema(officialMode, shopId, productTypeId, categoryId, extra = {}) {
  const body = { product_type_id_list: [Number(productTypeId)] };
  const cat = Number(categoryId);
  if (Number.isFinite(cat) && cat > 0) body.category_id = cat;
  if (officialMode) {
    if (extra.shopType) body.shopType = extra.shopType;
    if (extra.categoryPathLabel) body.categoryPathLabel = extra.categoryPathLabel;
    return adminAPI.getOfficialSheinCatalogAttributeTemplate(body);
  }
  return sheinPlatformAPI.getAttributeTemplate(shopId, body, { forceRefresh: true });
}

/**
 * 新建/编辑属性库：选类目 → 拉产品属性 → 填写 → 保存。
 */
export default function ProductAttributeTemplateCreateModal({
  open,
  shopId,
  templateId = null,
  templateScope = 'PERSONAL',
  officialMode = false,
  shopType = '',
  onClose,
  onSaved,
}) {
  const isEdit = Boolean(templateId);
  const pendingAttrsRef = useRef(null);
  const [name, setName] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [remark, setRemark] = useState('');
  const [autoRuleEnabled, setAutoRuleEnabled] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [productTypeId, setProductTypeId] = useState('');
  const [categoryPathLabel, setCategoryPathLabel] = useState('');
  const [attrLoading, setAttrLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [requiredAttrs, setRequiredAttrs] = useState([]);
  const [optionalAttrs, setOptionalAttrs] = useState([]);
  const [productAttributes, setProductAttributes] = useState({});
  const [attrsExpanded, setAttrsExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [metaLoading, setMetaLoading] = useState(false);
  const [customAttrPerm, setCustomAttrPerm] = useState({});
  const [templateVersion, setTemplateVersion] = useState(null);
  const [versionConflict, setVersionConflict] = useState(false);
  const [reloadingVersion, setReloadingVersion] = useState(false);

  const categoryOptions = useMemo(
    () => flattenLeafCategories(categories).map((opt) => ({
      value: opt.categoryId,
      label: opt.label,
      productTypeId: opt.productTypeId,
    })),
    [categories],
  );

  const allAttrs = useMemo(
    () => [...requiredAttrs, ...optionalAttrs],
    [requiredAttrs, optionalAttrs],
  );

  const visibleOptional = attrsExpanded ? optionalAttrs : optionalAttrs.slice(0, 9);

  useEffect(() => {
    if (!open) return;
    pendingAttrsRef.current = null;
    setName('');
    setStyleNo('');
    setRemark('');
    setAutoRuleEnabled(false);
    setSortOrder(0);
    setCategoryId('');
    setProductTypeId('');
    setCategoryPathLabel('');
    setRequiredAttrs([]);
    setOptionalAttrs([]);
    setProductAttributes({});
    setAttrsExpanded(false);
    setAttrLoading(false);
    setDetailLoading(false);
    setSaving(false);
    setCustomAttrPerm({});
    setTemplateVersion(null);
    setVersionConflict(false);
    setReloadingVersion(false);
  }, [open, templateId]);

  useEffect(() => {
    if (!open || (!officialMode && !shopId)) {
      setCategories([]);
      return undefined;
    }
    let cancelled = false;
    setMetaLoading(true);
    const load = officialMode
      ? adminAPI.getOfficialSheinCatalogCategories({ shopType })
      : sheinPlatformAPI.getShopCategories(shopId);
    load
      .then((res) => {
        if (cancelled) return;
        setCategories(res?.categories ?? res?.items ?? res?.list ?? res ?? []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      })
      .finally(() => {
        if (!cancelled) setMetaLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, officialMode, shopType]);

  useEffect(() => {
    if (!open || !templateId || (!officialMode && !shopId)) return undefined;
    let cancelled = false;
    setDetailLoading(true);
    fetchAttrTemplateDetail(officialMode, shopId, templateId, templateScope)
      .then((res) => {
        if (cancelled) return;
        const item = res?.item || res || {};
        const payload = item.payload && typeof item.payload === 'object' ? item.payload : {};
        setName(String(item.name || '').trim());
        setStyleNo(String(item.styleNo || '').trim());
        setRemark(String(item.remark || '').trim());
        setAutoRuleEnabled(item.autoRuleEnabled === true || item.autoRuleEnabled === 1);
        {
          const order = Number(item.sortOrder);
          setSortOrder(Number.isFinite(order) ? order : 0);
        }
        setCategoryId(String(item.categoryId || payload.categoryId || ''));
        setProductTypeId(String(item.productTypeId || payload.productTypeId || ''));
        setCategoryPathLabel(String(item.categoryPathLabel || payload.categoryPathLabel || ''));
        setTemplateVersion(parseTemplateVersion(item.version));
        setVersionConflict(false);
        pendingAttrsRef.current = payload.productAttributes && typeof payload.productAttributes === 'object'
          ? payload.productAttributes
          : {};
      })
      .catch((e) => {
        if (cancelled) return;
        showToast(e?.message || fixedUi("加载模板失败"), 'error');
        onClose?.();
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, templateId, templateScope, officialMode, onClose]);

  useEffect(() => {
    if (!open || (!officialMode && !shopId) || !productTypeId) {
      setRequiredAttrs([]);
      setOptionalAttrs([]);
      if (!templateId) setProductAttributes({});
      setAttrLoading(false);
      return undefined;
    }
    let cancelled = false;
    setAttrLoading(true);
    // 与发品页一致：整包响应交给 parseAttributeTemplate（勿先取 .data，否则会丢 attribute_infos）
    fetchCategoryAttributeSchema(officialMode, shopId, productTypeId, categoryId, {
      shopType,
      categoryPathLabel,
    })
      .then((raw) => {
        if (cancelled) return;
        const tpl = parseAttributeTemplate(raw, { productTypeId });
        const { requiredAttrs: required, optionalAttrs: optional } = partitionProductAttrsForDisplay(tpl);
        setRequiredAttrs(required);
        setOptionalAttrs(optional);
        const seeded = pendingAttrsRef.current;
        pendingAttrsRef.current = null;
        setProductAttributes(
          seeded && typeof seeded === 'object'
            ? alignAttributesToCategoryTemplate(seeded, tpl)
            : {},
        );
        if (!(required.length || optional.length)) {
          const saleCount = (tpl?.skcSaleAttrs?.length || 0) + (tpl?.skuSaleAttrs?.length || 0);
          const sizeCount = tpl?.sizeAttrs?.length || 0;
          if (saleCount || sizeCount) {
            showToast(
              `该类目暂无商品属性，仅有销售属性 ${saleCount} / 尺寸属性 ${sizeCount}`,
              'warning',
            );
          } else {
            showToast(fixedUi("平台未返回该类目属性，请确认类目或先同步店铺类目"), 'warning');
          }
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setRequiredAttrs([]);
        setOptionalAttrs([]);
        showToast(e?.message || '加载类目属性失败', 'error');
      })
      .finally(() => {
        if (!cancelled) setAttrLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, productTypeId, categoryId, categoryPathLabel, officialMode, shopType, templateId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  useEffect(() => {
    if (!open || !shopId || !categoryId) {
      setCustomAttrPerm({});
      return undefined;
    }
    let cancelled = false;
    sheinPlatformAPI.getCustomAttributePermission(shopId, {
      category_id_list: [Number(categoryId)],
    })
      .then((res) => {
        if (cancelled) return;
        setCustomAttrPerm(parseCustomAttributePermission(res));
      })
      .catch(() => {
        if (!cancelled) setCustomAttrPerm({});
      });
    return () => { cancelled = true; };
  }, [open, shopId, categoryId]);

  if (!open) return null;

  const onCategoryChange = (id) => {
    const hit = categoryOptions.find((opt) => String(opt.value) === String(id));
    setCategoryId(id ? String(id) : '');
    const nextTypeId = hit?.productTypeId && String(hit.productTypeId) !== '0'
      ? String(hit.productTypeId)
      : '';
    setProductTypeId(nextTypeId);
    setCategoryPathLabel(hit?.label || '');
    if (id && !nextTypeId) {
      showToast(fixedUi("该类目缺少 product_type_id，无法查询属性，请换一个末级类目"), 'warning');
    }
    if (!isEdit && nextTypeId && (officialMode || shopId)) {
      fetchAttrTemplateList(officialMode, shopId, {
        scope: templateScope,
        productTypeId: nextTypeId,
        page: 1,
        pageSize: 100,
        ...(officialMode ? { shopType } : {}),
      }).then((res) => {
        setSortOrder(nextTemplatePriority(res?.items));
      }).catch(() => {
        setSortOrder(0);
      });
    }
  };

  const setAttrValue = (attributeId, value) => {
    setProductAttributes((prev) => ({
      ...prev,
      [attributeId]: value,
    }));
  };

  const appendCustomAttrValue = (attributeId, opt) => {
    if (!attributeId || !opt?.id) return;
    const patchList = (list) => (list || []).map((attr) => {
      if (String(attr.attributeId) !== String(attributeId)) return attr;
      if ((attr.values || []).some((v) => String(v.id) === String(opt.id))) return attr;
      return {
        ...attr,
        values: [...(attr.values || []), {
          id: String(opt.id),
          name: opt.name,
          nameCn: opt.nameCn || opt.name,
        }],
      };
    });
    setRequiredAttrs((prev) => patchList(prev));
    setOptionalAttrs((prev) => patchList(prev));
  };

  const applyAttributeTemplateItem = (item) => {
    const payload = item.payload && typeof item.payload === 'object' ? item.payload : {};
    setName(String(item.name || '').trim());
    setStyleNo(String(item.styleNo || '').trim());
    setRemark(String(item.remark || '').trim());
    setAutoRuleEnabled(item.autoRuleEnabled === true || item.autoRuleEnabled === 1);
    {
      const order = Number(item.sortOrder);
      setSortOrder(Number.isFinite(order) ? order : 0);
    }
    setCategoryId(String(item.categoryId || payload.categoryId || ''));
    setProductTypeId(String(item.productTypeId || payload.productTypeId || ''));
    setCategoryPathLabel(String(item.categoryPathLabel || payload.categoryPathLabel || ''));
    setTemplateVersion(parseTemplateVersion(item.version));
    pendingAttrsRef.current = payload.productAttributes && typeof payload.productAttributes === 'object'
      ? payload.productAttributes
      : {};
  };

  const reloadTemplateFromServer = async () => {
    if (!templateId || (!officialMode && !shopId)) return;
    setReloadingVersion(true);
    try {
      const res = await fetchAttrTemplateDetail(officialMode, shopId, templateId, templateScope);
      const item = res?.item || res || {};
      applyAttributeTemplateItem(item);
      const ptId = String(item.productTypeId || item.payload?.productTypeId || '');
      const catId = String(item.categoryId || item.payload?.categoryId || '');
      if (ptId && (officialMode || shopId)) {
        const raw = await fetchCategoryAttributeSchema(officialMode, shopId, ptId, catId, {
          shopType,
          categoryPathLabel: String(item.categoryPathLabel || item.payload?.categoryPathLabel || ''),
        });
        const tpl = parseAttributeTemplate(raw, { productTypeId: ptId });
        const { requiredAttrs: required, optionalAttrs: optional } = partitionProductAttrsForDisplay(tpl);
        setRequiredAttrs(required);
        setOptionalAttrs(optional);
        const seeded = pendingAttrsRef.current;
        pendingAttrsRef.current = null;
        setProductAttributes(
          seeded && typeof seeded === 'object'
            ? alignAttributesToCategoryTemplate(seeded, tpl)
            : {},
        );
      }
      setVersionConflict(false);
      showToast(fixedUi("已重新加载属性库"), 'success');
    } catch (e) {
      showToast(e?.message || fixedUi("重新加载失败"), 'error');
    } finally {
      setReloadingVersion(false);
    }
  };

  const submit = async () => {
    if (!officialMode && !shopId) {
      showToast(fixedUi("请先选择店铺"), 'error');
      return;
    }
    const styleNoValue = String(styleNo || '').trim();
    if (!styleNoValue) {
      showToast(fixedUi("请填写款号"), 'error');
      return;
    }
    const title = String(name || '').trim();
    if (!title) {
      showToast(fixedUi("请填写模板名称"), 'error');
      return;
    }
    if (!productTypeId) {
      showToast(fixedUi("请选择类目"), 'error');
      return;
    }
    if (attrLoading) {
      showToast(fixedUi("类目属性尚未加载完成，请稍后再保存"), 'warning');
      return;
    }
    if (!allAttrs.length && !countFilledProductAttributes(productAttributes)) {
      showToast(fixedUi("当前类目无可填产品属性"), 'error');
      return;
    }
    const payload = buildProductAttributeTemplatePayload({
      productAttributes,
      attrDefs: allAttrs,
      productTypeId,
      categoryId,
      categoryPathLabel,
    });
    if (!countFilledProductAttributes(payload.productAttributes)) {
      showToast(fixedUi("请至少填写一项类目属性"), 'error');
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: title,
        styleNo: styleNoValue,
        remark: String(remark || '').trim() || undefined,
        productTypeId,
        categoryId,
        categoryPathLabel,
        autoRuleEnabled,
        sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
        payload,
        ...(isEdit || officialMode ? {} : { scope: templateScope }),
      };
      if (officialMode) {
        body.shopType = shopType;
      }
      if (isEdit && templateVersion != null) {
        body.version = templateVersion;
      }
      const res = await saveAttrTemplate(officialMode, shopId, isEdit ? templateId : null, body);
      if (res?.item && parseTemplateVersion(res.item.version) != null) {
        setTemplateVersion(parseTemplateVersion(res.item.version));
      }
      setVersionConflict(false);
      showToast(isEdit ? fixedUi("模板已更新") : fixedUi("模板已创建"), 'success');
      onSaved?.();
      onClose?.();
    } catch (e) {
      if (isEdit && isTemplateVersionConflictError(e)) {
        showToast(conflictToastMessage([{ type: 'attribute' }]), 'warning', 3000);
        setVersionConflict(true);
      } else {
        showToast(e?.message || (isEdit ? fixedUi("更新失败") : '创建失败'), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const renderAttrGroup = (title, list, required = false) => {
    if (!list.length) return null;
    const slots = layoutAttributeFields(list);
    return (
      <>
        <h4 className="spo-publish__subhead">{title}</h4>
        <div className="spo-wizard__form spo-wizard__form--attrs">
          {slots.map(({ attr, fullWidth }) => (
            <AttributeField
              key={attr.attributeId}
              attr={attr}
              required={required}
              fullWidth={fullWidth}
              shopId={shopId}
              categoryId={categoryId}
              allowCustomValue={!!customAttrPerm[String(attr.attributeId)]}
              value={productAttributes[attr.attributeId]}
              onChange={(v) => setAttrValue(attr.attributeId, v)}
              onCustomValueAdded={appendCustomAttrValue}
            />
          ))}
        </div>
      </>
    );
  };

  return createPortal(
    <div className="spo-batch-modal spo-attr-tpl-modal" role="presentation">
      <button
        type="button"
        className="spo-batch-modal__backdrop"
        aria-label={fixedUi("关闭")}
        onClick={() => { if (!saving) onClose?.(); }}
      />
      <div
        className="spo-batch-modal__panel spo-attr-tpl-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-attr-tpl-create-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-attr-tpl-create-title">
            {isEdit
              ? (officialMode ? '编辑官方属性模板' : '编辑属性库')
              : (officialMode ? '新建官方属性模板' : '新建属性库')}
          </h2>
          <button type="button" className="spo-batch-modal__close" aria-label={fixedUi("关闭")} disabled={saving} onClick={onClose}>×</button>
        </div>
        <div className="spo-batch-modal__body">
          <div className="spo-wizard__form spo-template-create-form">
            <label className="spo-field spo-field--full">
              <span className="spo-field__label">{fixedUi("款号")} <RequiredMark /></span>
              <input
                className="spo-input"
                value={styleNo}
                disabled={saving || detailLoading}
                maxLength={128}
                placeholder={fixedUi("请输入款号")}
                onChange={(e) => setStyleNo(e.target.value)}
              />
            </label>
            <label className={`spo-field spo-field--full${versionConflict ? ' spo-field--version-conflict' : ''}`}>
              <span className="spo-field__label">
                {fixedUi("模板名称")} <RequiredMark />
                <TemplateVersionConflictMark
                  active={versionConflict}
                  label={fixedUi("已更新")}
                  reloading={reloadingVersion}
                  onReload={reloadTemplateFromServer}
                />
              </span>
              <input
                className="spo-input"
                value={name}
                disabled={saving || detailLoading}
                maxLength={128}
                placeholder={fixedUi("如：女装夏季基础属性")}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="spo-field spo-field--full">
              <span className="spo-field__label">{fixedUi("备注")}</span>
              <input
                className="spo-input"
                value={remark}
                disabled={saving || detailLoading}
                maxLength={200}
                placeholder={fixedUi("可选")}
                onChange={(e) => setRemark(e.target.value)}
              />
            </label>
            <div className="spo-field spo-field--full spo-template-auto-rule">
              <div className="spo-template-auto-rule__switch">
                <SheinSwitch
                  checked={autoRuleEnabled}
                  disabled={saving || detailLoading}
                  ariaLabel={fixedUi("自动规则")}
                  onChange={setAutoRuleEnabled}
                />
                <span>{fixedUi("自动规则")}</span>
              </div>
              <p className="spo-wizard__inline-hint">
                开启后，发品选中该类目时自动带出。优先级仅在同类目内比较，数字越小越优先；同类目不可重复，不同类目互不影响。
              </p>
              <label className="spo-field">
                <span className="spo-field__label">{fixedUi("优先级")}</span>
                <input
                  className="spo-input"
                  type="number"
                  step={1}
                  value={sortOrder}
                  disabled={saving || detailLoading}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setSortOrder(Number.isFinite(next) ? next : 0);
                  }}
                />
              </label>
            </div>
            <label className="spo-field spo-field--full">
              <span className="spo-field__label">{fixedUi("适用类目")} <RequiredMark /></span>
              <CategoryCascaderSelect
                categories={categories}
                value={categoryId}
                label={categoryPathLabel}
                disabled={saving || detailLoading || metaLoading || (!officialMode && !shopId)}
                placeholder={
                  officialMode
                    ? (metaLoading || detailLoading ? '加载类目中…' : fixedUi("请选择或搜索类目"))
                    : (!shopId ? fixedUi("请先选择店铺") : (metaLoading || detailLoading ? fixedUi("加载中…") : fixedUi("请选择或搜索类目")))
                }
                onChange={onCategoryChange}
              />
            </label>
          </div>

          {detailLoading ? (
            <p className="spo-wizard__inline-hint">{fixedUi("正在加载模板…")}</p>
          ) : null}

          {attrLoading && productTypeId ? (
            <p className="spo-wizard__inline-hint">{fixedUi("正在加载类目属性…")}</p>
          ) : null}

          {!attrLoading && productTypeId ? (
            <div className="spo-template-create-attrs">
              {renderAttrGroup('必填属性', requiredAttrs, true)}
              {renderAttrGroup('选填属性', visibleOptional)}
              {optionalAttrs.length > 9 ? (
                <div className="spo-publish__expand">
                  <button
                    type="button"
                    className="spo-btn spo-btn--ghost spo-btn--sm"
                    disabled={saving}
                    onClick={() => setAttrsExpanded((v) => !v)}
                  >
                    {attrsExpanded ? '收起' : `展开全部（${optionalAttrs.length - 9}）`}
                  </button>
                </div>
              ) : null}
              {!allAttrs.length ? (
                <p className="spo-wizard__inline-hint">{fixedUi("当前类目暂无可填属性")}</p>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" disabled={saving} onClick={onClose}>
            {fixedUi("取消")}
          </button>
          <button
            type="button"
            className="spo-btn spo-btn--primary spo-btn--sm"
            disabled={saving || detailLoading || (!officialMode && !shopId)}
            onClick={submit}
          >
            {saving ? fixedUi("保存中…") : (isEdit ? '保存修改' : fixedUi("保存"))}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
